// file: src/components/TtsBenchmark.jsx
// Story Guide Simulator + TTS Benchmark — side by side

import React, { useState, useRef } from 'react';

// ── Story prompt (mirrors backend promptConfig.js storyGuide template) ─────────
function buildStoryGuidePrompt({ poiName, poiDescription, userQuestion, language, wordCount, chipCount }) {
  const languageMap = {
    'en': 'English',
    'pt': 'European Portuguese (Portugal). Use natural expressions and vocabulary that sound authentic to Portuguese from Portugal, not Brazilian Portuguese',
    'br': 'Brazilian Portuguese',
    'fr': 'French', 'es': 'Spanish', 'bg': 'Bulgarian', 'de': 'German',
  };
  const targetLanguage = languageMap[language] || 'English';
  const visitorInstructions = 'AUDIENCE: An adult visitor. Use natural conversational language with solid historical and cultural depth.';
  const stopDescSection = poiDescription.trim() ? `STOP DESCRIPTION:\n${poiDescription.trim()}\n\n` : '';

  return `You MUST respond entirely in ${targetLanguage}.\n\n` +
    `STOP: ${poiName}\n` +
    `${stopDescSection}` +
    `=== VISITOR PROFILE (MANDATORY — follow all instructions below exactly) ===\n` +
    `${visitorInstructions}\n` +
    `=== END VISITOR PROFILE ===\n` +
    `\nCURRENT QUESTION/TOPIC: ${userQuestion}\n\n` +
    `TASK:\n` +
    `1. Answer in ${wordCount || '140 to 200'} words, strictly following the visitor profile above.\n` +
    `2. Then — without announcing it — add a JSON array of follow-up questions.\n` +
    `   Number of questions: ${chipCount || '6 to 8'}.\n` +
    `   The questions MUST be specific to "${poiName}".\n` +
    `   Cover different aspects — vary the angle (people involved, key events, physical details, myths, legacy).\n` +
    `   BAD (generic): ["Tell me more", "What else is interesting?"]\n` +
    `   GOOD (specific): ["Who funded the construction?", "What happened to it in 1944?"]\n` +
    `Example output format:\n` +
    `...your story text here...\n` +
    `["Specific question 1?", "Specific question 2?"]`;
}

function parseStoryResponse(raw) {
  const jsonStart = raw.lastIndexOf('[');
  const jsonEnd = raw.lastIndexOf(']');
  let chips = [];
  let text = raw.trim();
  if (jsonStart !== -1 && jsonEnd > jsonStart) {
    try {
      const jsonStr = raw.substring(jsonStart, jsonEnd + 1);
      const decoded = JSON.parse(jsonStr);
      chips = decoded.map(e => String(e).trim()).filter(Boolean);
      text = raw.substring(0, jsonStart).trim();
    } catch (_) {}
  }
  return { text, chips };
}

// ── TTS providers ──────────────────────────────────────────────────────────────
const AZURE_KEY = 'C3sfNJMrNGIlYBjcvsQrHYhgPf4jQOhlNsJhgLUDsSOBsX9LaaZFJQQJ99BCACYeBjFXJ3w3AAAYACOGaIJJ';
const AZURE_REGION = 'eastus';

const AZURE_VOICES = { 'en': 'en-US-JennyNeural', 'pt': 'pt-PT-RaquelNeural', 'br': 'pt-BR-FranciscaNeural', 'fr': 'fr-FR-DeniseNeural', 'es': 'es-ES-ElviraNeural', 'de': 'de-DE-KatjaNeural', 'bg': 'bg-BG-KalinaNeural' };
const AZURE_VOICES_FERNANDA = { ...AZURE_VOICES, 'pt': 'pt-PT-FernandaNeural' };
const AZURE_VOICES_DUARTE = { ...AZURE_VOICES, 'pt': 'pt-PT-DuarteNeural', 'en': 'en-US-GuyNeural' };

const langToAzureLang = { 'en': 'en-US', 'pt': 'pt-PT', 'br': 'pt-BR', 'fr': 'fr-FR', 'es': 'es-ES', 'de': 'de-DE', 'bg': 'bg-BG' };

const makeAzureCall = async (text, lang, voiceName) => {
  const azureLang = langToAzureLang[lang] || 'en-US';
  const ssml = `<speak version='1.0' xml:lang='${azureLang}'><voice name='${voiceName}'>${text}</voice></speak>`;
  const res = await fetch(`https://${AZURE_REGION}.tts.speech.microsoft.com/cognitiveservices/v1`, {
    method: 'POST',
    headers: {
      'Ocp-Apim-Subscription-Key': AZURE_KEY,
      'Content-Type': 'application/ssml+xml',
      'X-Microsoft-OutputFormat': 'audio-24khz-48kbitrate-mono-mp3',
    },
    body: ssml,
  });
  if (!res.ok) throw new Error(`Azure ${res.status}: ${await res.text()}`);
  return { buffer: await res.arrayBuffer(), mime: 'audio/mp3' };
};

const PROVIDERS = [
  {
    id: 'azure-raquel',
    label: 'Azure · Raquel',
    getSubLabel: (lang) => `${AZURE_VOICES[lang] || AZURE_VOICES['en']} · current app voice`,
    color: '#0078d4',
    call: async (text, keys, lang) => makeAzureCall(text, lang, AZURE_VOICES[lang] || AZURE_VOICES['en']),
  },
  {
    id: 'azure-fernanda',
    label: 'Azure · Fernanda',
    getSubLabel: (lang) => `${AZURE_VOICES_FERNANDA[lang] || AZURE_VOICES_FERNANDA['en']}`,
    color: '#0ea5e9',
    call: async (text, keys, lang) => makeAzureCall(text, lang, AZURE_VOICES_FERNANDA[lang] || AZURE_VOICES_FERNANDA['en']),
  },
  {
    id: 'azure-duarte',
    label: 'Azure · Duarte',
    getSubLabel: (lang) => `${AZURE_VOICES_DUARTE[lang] || AZURE_VOICES_DUARTE['en']} · male`,
    color: '#38bdf8',
    call: async (text, keys, lang) => makeAzureCall(text, lang, AZURE_VOICES_DUARTE[lang] || AZURE_VOICES_DUARTE['en']),
  },
  ...([
    { id: 'azure-ava',         voice: 'en-US-AvaMultilingualNeural',        label: 'Ava',         color: '#6366f1' },
    { id: 'azure-emma',        voice: 'en-US-EmmaMultilingualNeural',        label: 'Emma',        color: '#818cf8' },
    { id: 'azure-amanda',      voice: 'en-US-AmandaMultilingualNeural',      label: 'Amanda',      color: '#a78bfa' },
    { id: 'azure-phoebe',      voice: 'en-US-PhoebeMultilingualNeural',      label: 'Phoebe',      color: '#c4b5fd' },
    { id: 'azure-serena',      voice: 'en-US-SerenaMultilingualNeural',      label: 'Serena',      color: '#6d28d9' },
    { id: 'azure-jenny-multi', voice: 'en-US-JennyMultilingualNeural',       label: 'Jenny',       color: '#5b21b6' },
    { id: 'azure-andrew',      voice: 'en-US-AndrewMultilingualNeural',      label: 'Andrew ♂',    color: '#2563eb' },
    { id: 'azure-brian',       voice: 'en-US-BrianMultilingualNeural',       label: 'Brian ♂',     color: '#1d4ed8' },
    { id: 'azure-alloy-turbo', voice: 'en-US-AlloyTurboMultilingualNeural',  label: 'Alloy Turbo', color: '#f59e0b' },
    { id: 'azure-nova-turbo',  voice: 'en-US-NovaTurboMultilingualNeural',   label: 'Nova Turbo',  color: '#d97706' },
    { id: 'azure-echo-turbo',  voice: 'en-US-EchoTurboMultilingualNeural',   label: 'Echo Turbo ♂',color: '#b45309' },
    { id: 'azure-onyx-turbo',  voice: 'en-US-OnyxTurboMultilingualNeural',   label: 'Onyx Turbo ♂',color: '#92400e' },
  ].map(({ id, voice, label, color }) => ({
    id, label: `Azure · ${label}`,
    getSubLabel: () => `${voice} · multilingual`,
    color,
    enOnly: false,
    call: async (text, keys, lang) => makeAzureCall(text, lang, voice),
  }))),
  {
    id: 'elevenlabs-turbo',
    label: 'ElevenLabs Turbo',
    getSubLabel: () => 'eleven_turbo_v2_5 · Rachel',
    color: '#f97316',
    call: async (text, keys, lang) => {
      if (!keys.elevenlabs) throw new Error('Enter ElevenLabs API key');
      const langHint = lang === 'pt' ? 'You MUST speak in European Portuguese from Portugal. ' : '';
      const res = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/21m00Tcm4TlvDq8ikWAM`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'xi-api-key': keys.elevenlabs },
        body: JSON.stringify({ text: langHint + text, model_id: 'eleven_turbo_v2_5', voice_settings: { stability: 0.5, similarity_boost: 0.75 } }),
      });
      if (!res.ok) throw new Error(`ElevenLabs ${res.status}`);
      return { buffer: await res.arrayBuffer(), mime: 'audio/mpeg' };
    },
  },
  {
    id: 'elevenlabs-multilingual',
    label: 'ElevenLabs Multilingual',
    getSubLabel: () => 'eleven_multilingual_v2 · Rachel',
    color: '#ea580c',
    call: async (text, keys, lang) => {
      if (!keys.elevenlabs) throw new Error('Enter ElevenLabs API key');
      const langHint = lang === 'pt' ? 'You MUST speak in European Portuguese from Portugal. ' : '';
      const res = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/21m00Tcm4TlvDq8ikWAM`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'xi-api-key': keys.elevenlabs },
        body: JSON.stringify({ text: langHint + text, model_id: 'eleven_multilingual_v2', voice_settings: { stability: 0.5, similarity_boost: 0.75 } }),
      });
      if (!res.ok) throw new Error(`ElevenLabs ${res.status}`);
      return { buffer: await res.arrayBuffer(), mime: 'audio/mpeg' };
    },
  },
  {
    id: 'gemini-flash',
    label: 'Gemini Flash TTS',
    getSubLabel: () => 'gemini-2.5-flash-tts · Aoede',
    color: '#4285f4',
    call: async (text, keys, lang) => {
      if (!keys.gemini) throw new Error('Enter Gemini API key');
      const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-tts:generateContent?key=${keys.gemini}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text }] }],
          generationConfig: { responseModalities: ['AUDIO'], speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Aoede' } } } },
        }),
      });
      if (!res.ok) throw new Error(`Gemini ${res.status}`);
      const data = await res.json();
      const b64 = data.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
      if (!b64) throw new Error('No audio in Gemini response');
      const binary = atob(b64);
      const buf = new ArrayBuffer(binary.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < binary.length; i++) view[i] = binary.charCodeAt(i);
      return { buffer: buf, mime: 'audio/wav' };
    },
  },
  {
    id: 'openai-nova',
    label: 'OpenAI TTS',
    getSubLabel: () => 'tts-1 · nova',
    color: '#10a37f',
    call: async (text, keys, lang) => {
      if (!keys.openai) throw new Error('Enter OpenAI API key');
      const res = await fetch('https://api.openai.com/v1/audio/speech', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${keys.openai}` },
        body: JSON.stringify({ model: 'tts-1', input: text, voice: 'nova' }),
      });
      if (!res.ok) throw new Error(`OpenAI ${res.status}`);
      return { buffer: await res.arrayBuffer(), mime: 'audio/mpeg' };
    },
  },
];

const formatMs = (ms) => ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`;
const latencyColor = (ms) => ms < 1500 ? '#4ade80' : ms < 3000 ? '#facc15' : '#f87171';

const LANG_OPTIONS = [
  { code: 'en', label: '🇬🇧 English' },
  { code: 'pt', label: '🇵🇹 Portuguese (PT)' },
  { code: 'br', label: '🇧🇷 Portuguese (BR)' },
  { code: 'fr', label: '🇫🇷 French' },
  { code: 'es', label: '🇪🇸 Spanish' },
  { code: 'de', label: '🇩🇪 German' },
];

const OPENING_PROMPT = 'Open with a warm, brief greeting, then immediately flow into one fascinating hook or fact about this place. No pause between greeting and story.';

// ── Styles ─────────────────────────────────────────────────────────────────────
const S = {
  page: { minHeight: '100vh', background: '#0f172a', color: '#f1f5f9', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column' },
  header: { padding: '20px 24px 12px', borderBottom: '1px solid #1e293b' },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  panel: { flex: 1, padding: '20px 20px 24px', overflowY: 'auto', minWidth: 0 },
  divider: { width: 1, background: '#1e293b', flexShrink: 0 },
  label: { fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 },
  input: { width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 13, padding: '8px 12px', outline: 'none' },
  textarea: { width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 13, padding: '8px 12px', outline: 'none', resize: 'vertical', lineHeight: 1.6 },
  btn: (color, disabled) => ({ background: disabled ? '#1e293b' : color, color: disabled ? '#475569' : '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer' }),
  card: (accent) => ({ background: '#1e293b', borderRadius: 12, padding: '14px 16px', border: `1px solid ${accent || '#334155'}`, marginBottom: 10 }),
  chip: { display: 'inline-block', background: '#0f172a', border: '1px solid #334155', borderRadius: 20, padding: '5px 12px', fontSize: 12, color: '#94a3b8', margin: '3px 4px 3px 0', cursor: 'pointer' },
};

export default function TtsBenchmark() {
  // ── Shared state ──────────────────────────────────────────────────────────────
  const [lang, setLang] = useState('en');
  const [keys, setKeys] = useState({
    openai: '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',
    elevenlabs: 'sk_238e386598f98b8f256f35e399f70d6853ec3a30114972cd',
    gemini: process.env.REACT_APP_GOOGLE_API_KEY || '',
  });

  // ── Story state ───────────────────────────────────────────────────────────────
  const [poiName, setPoiName] = useState('Colosseum');
  const [poiDesc, setPoiDesc] = useState('');
  const [userQuestion, setUserQuestion] = useState(OPENING_PROMPT);
  const [isOpening, setIsOpening] = useState(true);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyResult, setStoryResult] = useState(null);
  const [storyError, setStoryError] = useState(null);
  const [history, setHistory] = useState([]);
  const [usedChips, setUsedChips] = useState(new Set());
  // cardAudio[cardIndex] = { status: 'loading'|'done'|'error', audioSrc, latencyMs, voiceName, error }
  const [cardAudio, setCardAudio] = useState({});

  // ── TTS state ─────────────────────────────────────────────────────────────────
  const [ttsText, setTtsText] = useState('');
  const [ttsResults, setTtsResults] = useState({});
  const [ttsLoading, setTtsLoading] = useState({});
  const audioRefs = useRef({});

  // ── Story generation ──────────────────────────────────────────────────────────
  const generateStory = async (question, historyForCall) => {
    if (!question.trim() || !poiName.trim()) return;
    setStoryLoading(true);
    setStoryError(null);
    const start = Date.now();

    const prompt = buildStoryGuidePrompt({
      poiName: poiName.trim(),
      poiDescription: poiDesc.trim(),
      userQuestion: question.trim(),
      language: lang,
      wordCount: isOpening ? '140 to 200' : '70 to 100',
      chipCount: isOpening ? '6 to 8' : '1',
    });

    const messages = [
      ...(historyForCall || []).map(h => ({ role: h.role, content: h.content })),
      { role: 'user', content: prompt },
    ];

    // Capture card index before state update
    const cardIdx = isOpening ? 0 : (storyResult?.cards?.length ?? 0);

    // Optimistically add a streaming card so it appears immediately
    setStoryResult(prev => ({
      ...(isOpening ? { chips: [], cards: [] } : prev),
      cards: isOpening
        ? [{ text: '', question: null, streaming: true }]
        : [...(prev?.cards || []), { text: '', question: question.trim(), streaming: true }],
      chips: isOpening ? [] : (prev?.chips || []),
      latencyMs: null,
      completionTokens: null,
    }));

    try {
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${keys.openai}` },
        body: JSON.stringify({ model: 'gpt-4o-mini', max_tokens: isOpening ? 600 : 400, temperature: 0.7, messages, stream: true }),
      });
      if (!res.ok) throw new Error(`OpenAI ${res.status}: ${await res.text()}`);

      // Stream tokens — fire part-1 TTS as soon as we hit the first sentence
      // boundary past ~40% of estimated text, then part-2 when streaming ends.
      let raw = '';
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let firstTokenMs = null;
      let part1FiredAt = null; // char index where we split
      const voiceName = AZURE_VOICES_FERNANDA[lang] || AZURE_VOICES_FERNANDA['en'];
      const SPLIT_THRESHOLD = 200; // fire part-1 after this many chars at a sentence end

      // Mark card audio as split-loading immediately
      setCardAudio(prev => ({ ...prev, [cardIdx]: { split: true, voiceName, part1: { status: 'loading' }, part2: { status: 'waiting' } } }));

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          const trimmed = line.replace(/^data: /, '').trim();
          if (!trimmed || trimmed === '[DONE]') continue;
          try {
            const delta = JSON.parse(trimmed).choices?.[0]?.delta?.content;
            if (delta) {
              if (firstTokenMs === null) firstTokenMs = Date.now() - start;
              const nextRaw = raw + delta;
              raw = nextRaw;
              setStoryResult(prev => {
                const cards = [...(prev?.cards || [])];
                cards[cardIdx] = { ...cards[cardIdx], text: nextRaw };
                return { ...prev, cards };
              });

              // Fire part-1 TTS at the first sentence boundary after threshold
              if (part1FiredAt === null && raw.length >= SPLIT_THRESHOLD) {
                const match = raw.match(/^(.*?[.!?])\s+\S/s);
                if (match) {
                  part1FiredAt = match[1].length;
                  const part1Text = raw.substring(0, part1FiredAt).trim();
                  const p1Start = Date.now();
                  setCardAudio(prev => ({ ...prev, [cardIdx]: { ...prev[cardIdx], part1: { status: 'loading', firedAt: Date.now() - start } } }));
                  makeAzureCall(part1Text, lang, voiceName).then(({ buffer, mime }) => {
                    const blob = new Blob([buffer], { type: mime });
                    setCardAudio(prev => ({
                      ...prev,
                      [cardIdx]: { ...prev[cardIdx], part1: { status: 'done', audioSrc: URL.createObjectURL(blob), latencyMs: Date.now() - p1Start, chars: part1Text.length } },
                    }));
                  }).catch(e => {
                    setCardAudio(prev => ({ ...prev, [cardIdx]: { ...prev[cardIdx], part1: { status: 'error', error: e.message } } }));
                  });
                }
              }
            }
          } catch (_) {}
        }
      }

      const latencyMs = Date.now() - start;
      const { text, chips } = parseStoryResponse(raw);

      // Fire part-2 TTS for the remainder
      const part2Text = (part1FiredAt != null ? text.substring(part1FiredAt).trim() : text);
      const p2Start = Date.now();
      setCardAudio(prev => ({ ...prev, [cardIdx]: { ...prev[cardIdx], part2: { status: 'loading' } } }));
      makeAzureCall(part2Text, lang, voiceName).then(({ buffer, mime }) => {
        const blob = new Blob([buffer], { type: mime });
        setCardAudio(prev => ({
          ...prev,
          [cardIdx]: { ...prev[cardIdx], part2: { status: 'done', audioSrc: URL.createObjectURL(blob), latencyMs: Date.now() - p2Start, chars: part2Text.length } },
        }));
      }).catch(e => {
        setCardAudio(prev => ({ ...prev, [cardIdx]: { ...prev[cardIdx], part2: { status: 'error', error: e.message } } }));
      });

      const newHistory = [
        ...(historyForCall || []),
        { role: 'user', content: question.trim() },
        { role: 'assistant', content: text },
      ].slice(-20);

      setHistory(newHistory);
      setStoryResult(prev => {
        const prevChips = isOpening ? [] : (prev?.chips || []);
        const mergedChips = [...prevChips];
        for (const c of chips) {
          if (!mergedChips.includes(c)) mergedChips.push(c);
        }
        const cards = [...(prev?.cards || [])];
        cards[cardIdx] = { text, question: isOpening ? null : question.trim(), streaming: false };
        return {
          chips: isOpening ? chips : mergedChips,
          latencyMs,
          firstTokenMs,
          completionTokens: null,
          cards,
        };
      });

      setTtsText(text);
      setTtsResults({});
      setIsOpening(false);
    } catch (e) {
      setStoryError(e.message);
    } finally {
      setStoryLoading(false);
    }
  };

  const handleOpeningGenerate = () => {
    setStoryResult(null);
    setCardAudio({});
    setHistory([]);
    setUsedChips(new Set());
    setIsOpening(true);
    setUserQuestion(OPENING_PROMPT);
    generateStory(OPENING_PROMPT, []);
  };

  const handleChipClick = (chip) => {
    setUsedChips(prev => new Set([...prev, chip]));
    setUserQuestion(chip);
    setIsOpening(false);
    generateStory(chip, history);
  };

  const handleCustomQuestion = () => {
    if (!userQuestion.trim() || userQuestion === OPENING_PROMPT) return;
    setIsOpening(false);
    generateStory(userQuestion.trim(), history);
  };

  // ── TTS ───────────────────────────────────────────────────────────────────────
  const runProvider = async (provider) => {
    const text = ttsText.trim();
    if (!text) return;
    setTtsLoading(l => ({ ...l, [provider.id]: true }));
    setTtsResults(r => ({ ...r, [provider.id]: { status: 'loading' } }));
    const start = Date.now();
    try {
      const { buffer, mime } = await provider.call(text, keys, lang);
      const latencyMs = Date.now() - start;
      const blob = new Blob([buffer], { type: mime });
      setTtsResults(r => ({ ...r, [provider.id]: { status: 'done', latencyMs, audioSrc: URL.createObjectURL(blob), byteLength: buffer.byteLength, charCount: text.length } }));
    } catch (e) {
      setTtsResults(r => ({ ...r, [provider.id]: { status: 'error', error: e.message, latencyMs: Date.now() - start } }));
    } finally {
      setTtsLoading(l => ({ ...l, [provider.id]: false }));
    }
  };

  const runAllTts = () => PROVIDERS.forEach(p => runProvider(p));
  const stopAll = () => Object.values(audioRefs.current).forEach(el => el && (el.pause(), el.currentTime = 0));

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Story Guide Simulator</h1>
            <p style={{ color: '#475569', margin: '3px 0 0', fontSize: 12 }}>Mirrors the Flutter app's guide generation pipeline — AI text left, TTS comparison right</p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {LANG_OPTIONS.map(({ code, label }) => (
              <button key={code} onClick={() => { setLang(code); setStoryResult(null); setHistory([]); setTtsResults({}); }}
                style={{ padding: '5px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer', border: 'none', background: lang === code ? '#3b82f6' : '#1e293b', color: lang === code ? '#fff' : '#64748b' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* API Keys */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
          {[{ k: 'openai', label: 'OpenAI', ph: 'sk-...' }, { k: 'elevenlabs', label: 'ElevenLabs', ph: 'sk_...' }, { k: 'gemini', label: 'Gemini', ph: 'AIza...' }].map(({ k, label, ph }) => (
            <div key={k}>
              <label style={S.label}>{label}</label>
              <input type="password" value={keys[k]} onChange={e => setKeys(prev => ({ ...prev, [k]: e.target.value }))} placeholder={ph}
                style={{ ...S.input, borderColor: keys[k] ? '#22c55e44' : '#334155' }} />
            </div>
          ))}
        </div>
      </div>

      {/* Body — two panels */}
      <div style={S.body}>

        {/* ── LEFT: Story Generator ─────────────────────────────────────────────── */}
        <div style={{ ...S.panel, maxWidth: 480 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>POI Name</label>
            <input value={poiName} onChange={e => setPoiName(e.target.value)} placeholder="e.g. Colosseum" style={S.input} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>POI Description (optional)</label>
            <textarea value={poiDesc} onChange={e => setPoiDesc(e.target.value)} rows={4} placeholder="Paste stop description for grounded answers…" style={S.textarea} />
          </div>

          <button onClick={handleOpeningGenerate} disabled={storyLoading || !poiName.trim() || !keys.openai}
            style={{ ...S.btn('#3b82f6', storyLoading || !poiName.trim() || !keys.openai), marginBottom: 16, width: '100%' }}>
            {storyLoading && isOpening ? '⏳ Generating opening…' : '▶ Generate Opening'}
          </button>

          {storyError && (
            <div style={{ background: '#1c0a0a', border: '1px solid #7f1d1d', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 12, color: '#f87171' }}>{storyError}</div>
          )}

          {/* Story cards */}
          {storyResult?.cards?.map((card, i) => {
            const audio = cardAudio[i];
            const isLast = i === storyResult.cards.length - 1;
            return (
              <div key={i} style={S.card('#1e40af44')}>
                {card.question && <div style={{ fontSize: 12, color: '#60a5fa', fontWeight: 600, marginBottom: 6 }}>↳ {card.question}</div>}
                <p style={{ fontSize: 13, lineHeight: 1.7, color: '#cbd5e1', margin: 0, whiteSpace: 'pre-wrap' }}>{card.text}</p>

                {/* Audio player — split into two parallel parts */}
                {audio && (
                  <div style={{ marginTop: 12, background: '#0f172a', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#0ea5e9' }} />
                      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{audio.voiceName}</span>
                      <span style={{ fontSize: 10, color: '#475569' }}>· split TTS</span>
                    </div>
                    {['part1', 'part2'].map((part, pi) => {
                      const p = audio[part];
                      if (!p) return null;
                      return (
                        <div key={part} style={{ marginBottom: pi === 0 ? 6 : 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: p?.status === 'done' ? 4 : 0 }}>
                            <span style={{ fontSize: 10, color: '#475569', minWidth: 36 }}>Part {pi + 1}</span>
                            {p.status === 'waiting' && <span style={{ fontSize: 10, color: '#334155' }}>waiting for stream…</span>}
                            {p.status === 'loading' && (
                              <span style={{ fontSize: 10, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <div style={{ width: 8, height: 8, border: '2px solid #334155', borderTopColor: '#0ea5e9', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                                {pi === 0 ? 'fired mid-stream…' : 'fired after stream…'}
                              </span>
                            )}
                            {p.status === 'done' && <span style={{ fontSize: 10, color: latencyColor(p.latencyMs) }}>🔊 {formatMs(p.latencyMs)} · {p.chars} chars</span>}
                            {p.status === 'error' && <span style={{ fontSize: 10, color: '#f87171' }}>failed: {p.error}</span>}
                          </div>
                          {p.status === 'done' && <audio src={p.audioSrc} controls style={{ width: '100%', height: 28, borderRadius: 4 }} />}
                        </div>
                      );
                    })}
                  </div>
                )}

                {isLast && storyResult.latencyMs != null && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#475569', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span>✍️ first token: <span style={{ color: latencyColor(storyResult.firstTokenMs) }}>{storyResult.firstTokenMs != null ? formatMs(storyResult.firstTokenMs) : '—'}</span> · full text: <span style={{ color: '#94a3b8' }}>{formatMs(storyResult.latencyMs)}</span></span>
                    <button onClick={() => setTtsText(card.text)} style={{ fontSize: 11, color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      → push to TTS benchmark
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Loading indicator before first token */}
          {storyLoading && !(storyResult?.cards?.some(c => c.streaming && c.text)) && (
            <div style={{ color: '#64748b', fontSize: 12, padding: '6px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 10, height: 10, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Waiting for first token…
            </div>
          )}

          {/* Chips */}
          {storyResult?.chips?.length > 0 && !storyLoading && (
            <div style={{ marginTop: 8, marginBottom: 12 }}>
              <label style={S.label}>Follow-up questions</label>
              {storyResult.chips.filter(c => !usedChips.has(c)).map((chip, i) => (
                <span key={i} style={S.chip} onClick={() => handleChipClick(chip)}>{chip}</span>
              ))}
            </div>
          )}

          {/* Custom question */}
          {storyResult && !storyLoading && (
            <div style={{ marginTop: 8 }}>
              <label style={S.label}>Ask your own question</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={userQuestion === OPENING_PROMPT ? '' : userQuestion} onChange={e => setUserQuestion(e.target.value || OPENING_PROMPT)}
                  placeholder="Ask anything about this stop…" style={{ ...S.input, flex: 1 }}
                  onKeyDown={e => e.key === 'Enter' && handleCustomQuestion()} />
                <button onClick={handleCustomQuestion} disabled={storyLoading || !userQuestion.trim() || userQuestion === OPENING_PROMPT}
                  style={S.btn('#3b82f6', storyLoading || !userQuestion.trim() || userQuestion === OPENING_PROMPT)}>
                  {storyLoading ? '…' : 'Ask'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={S.divider} />

        {/* ── RIGHT: TTS Benchmark ──────────────────────────────────────────────── */}
        <div style={S.panel}>
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>Text to synthesise · {ttsText.trim().length} chars</label>
            <textarea value={ttsText} onChange={e => setTtsText(e.target.value)} rows={6} style={S.textarea} placeholder="Generate a story on the left, or paste text here…" />
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <button onClick={runAllTts} disabled={!ttsText.trim() || Object.values(ttsLoading).some(Boolean)}
              style={S.btn('#3b82f6', !ttsText.trim() || Object.values(ttsLoading).some(Boolean))}>
              {Object.values(ttsLoading).some(Boolean) ? 'Running…' : '▶ Run All TTS'}
            </button>
            <button onClick={stopAll} style={{ ...S.btn('#1e293b', false), border: '1px solid #334155', color: '#94a3b8' }}>■ Stop</button>
            <button onClick={() => { stopAll(); setTtsResults({}); }} style={{ ...S.btn('transparent', false), color: '#475569', border: '1px solid #1e293b' }}>Clear</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12 }}>
            {PROVIDERS.map(provider => {
              const result = ttsResults[provider.id];
              const isLoading = ttsLoading[provider.id];
              return (
                <div key={provider.id} style={{ background: '#1e293b', borderRadius: 12, padding: '14px 16px', border: `1px solid ${result?.status === 'done' ? provider.color + '44' : '#334155'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: provider.color }} />
                        <span style={{ fontWeight: 700, fontSize: 13 }}>{provider.label}</span>
                      </div>
                      <div style={{ color: '#475569', fontSize: 11, marginTop: 1, paddingLeft: 15 }}>{provider.getSubLabel(lang)}</div>
                    </div>
                    <button onClick={() => runProvider(provider)} disabled={isLoading || !ttsText.trim()}
                      style={{ background: provider.color + '18', color: provider.color, border: `1px solid ${provider.color}44`, borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (isLoading || !ttsText.trim()) ? 0.4 : 1 }}>
                      {isLoading ? '…' : 'Run'}
                    </button>
                  </div>

                  {!result && !isLoading && <div style={{ color: '#334155', fontSize: 12, textAlign: 'center', padding: '8px 0' }}>Not run</div>}
                  {isLoading && <div style={{ color: '#64748b', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}><div style={{ width: 12, height: 12, border: `2px solid #334155`, borderTopColor: provider.color, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Generating…</div>}
                  {result?.status === 'error' && <div style={{ background: '#1c0a0a', border: '1px solid #7f1d1d', borderRadius: 6, padding: '8px 10px', fontSize: 11, color: '#f87171' }}>Failed in {formatMs(result.latencyMs)}: {result.error}</div>}
                  {result?.status === 'done' && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                        <div style={{ background: '#0f172a', borderRadius: 6, padding: '6px 8px' }}>
                          <div style={{ color: '#475569', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Latency</div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: latencyColor(result.latencyMs) }}>{formatMs(result.latencyMs)}</div>
                        </div>
                        <div style={{ background: '#0f172a', borderRadius: 6, padding: '6px 8px' }}>
                          <div style={{ color: '#475569', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Size</div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#94a3b8' }}>{(result.byteLength / 1024).toFixed(0)} KB</div>
                        </div>
                      </div>
                      <audio ref={el => { audioRefs.current[provider.id] = el; }} src={result.audioSrc} controls style={{ width: '100%', height: 32, borderRadius: 5 }} />
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {/* Summary table */}
          {Object.values(ttsResults).filter(r => r?.status === 'done').length >= 2 && (
            <div style={{ marginTop: 32 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Latency ranking</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155', color: '#475569' }}>
                    {['Provider', 'Latency', 'Size'].map(h => <th key={h} style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'left' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {PROVIDERS
                    .filter(p => ttsResults[p.id]?.status === 'done')
                    .sort((a, b) => ttsResults[a.id].latencyMs - ttsResults[b.id].latencyMs)
                    .map((p, i) => {
                      const r = ttsResults[p.id];
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #1e293b', background: i === 0 ? '#0f2a1a' : 'transparent' }}>
                          <td style={{ padding: '8px 10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 6, height: 6, borderRadius: '50%', background: p.color }} />
                              <span style={{ fontWeight: 600 }}>{p.label}</span>
                              {i === 0 && <span style={{ fontSize: 9, background: '#166534', color: '#4ade80', borderRadius: 4, padding: '1px 5px', marginLeft: 4 }}>fastest</span>}
                            </div>
                          </td>
                          <td style={{ padding: '8px 10px', color: latencyColor(r.latencyMs), fontWeight: 700 }}>{formatMs(r.latencyMs)}</td>
                          <td style={{ padding: '8px 10px', color: '#94a3b8' }}>{(r.byteLength / 1024).toFixed(0)} KB</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } * { scrollbar-width: thin; scrollbar-color: #334155 transparent; }`}</style>
    </div>
  );
}
